import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  VerticalAlign,
  FrameAnchorType,
  FrameWrap,
  HeightRule,
  LineRuleType,
  ShadingType,
  TableLayoutType,
  TableAnchorType,
  OverlapType,
} from 'docx';
import { FormDocument, FormElement, ContentRun, PAGE_SIZE_PX } from '../store/types';

// Export model: the Form Designer canvas is a free-positioning surface (every
// element owns an absolute x/y/width/height), and this file's job is to get
// that exact arrangement into a .docx without re-deriving a layout from it.
// Earlier versions grouped elements into "lines" and emitted flowing
// paragraphs / row tables, which re-flowed the page (labels and their boxes
// drifted apart, section borders vanished). Instead, every element becomes a
// Word *frame* (`w:framePr`, page-anchored, absolute x/y in twips) holding an
// ordinary paragraph:
//   * position and size are the canvas' own numbers -- nothing is inferred;
//   * frame paragraphs are plain top-level <w:p>, so everything downstream
//     that walks `doc.paragraphs` (in-place $placeholder$ substitution, the
//     checkbox matcher, PDF conversion) keeps working unchanged;
//   * rect / hline / vline (previously dropped) are drawn as border-only
//     frames, which is what restores the section boxes and dividers.
// Nothing is ever combined into a shared paragraph. The one thing that needed
// care is the backend's checkbox matcher (extract_checkbox_groups in
// template_ivp New_format.py), which reads a question and its options out of
// ONE paragraph: each checkbox/radio frame therefore carries its question as a
// hidden run (see the note above buildLineCells). Real `table` elements stay
// real Word tables (row/col span intact), pinned to their canvas position as
// floating tables.
//
// Known limits: rotation is not applied; a frame is placed by `w:framePr`, which
// Word and LibreOffice both honour, but the fonts named in the design must be
// present (or metric-compatible) wherever it is rendered for text widths to
// match.

const PX_TO_TWIPS = 15; // 96dpi px -> twips (1px = 0.75pt = 15 twips); also the page/margin convention
const PX_TO_POINTS = 0.75;
const PX_TO_HALF_POINTS = 1.5;
const LINE_HEIGHT = 1.15; // ElementRenderer fixes line-height at 1.15 for text-like elements
const MIN_FRAME_DXA = 30; // ~2px floor so a degenerate/zero-size frame never produces invalid XML
const MAX_LINE_DXA = 31680; // Word's "Exactly" line-spacing ceiling (1584pt)
const MIN_LINE_DXA = 20; // ...and its floor (1pt)
const DEFAULT_FONT_PX = 12; // ElementRenderer's fallback when style.fontSize is unset
const TABLE_CELL_FONT_PX = 12; // ElementRenderer hard-codes 12px inside table cells
const TABLE_CELL_PAD_PX = 4;

type DocxBorderStyle = (typeof BorderStyle)[keyof typeof BorderStyle];

interface BorderSide {
  style: DocxBorderStyle;
  color?: string;
  size?: number;
  space?: number;
}

interface BorderSet {
  top: BorderSide;
  bottom: BorderSide;
  left: BorderSide;
  right: BorderSide;
}

interface TextStyle {
  font?: string;
  sizePx: number;
  bold: boolean;
  italics: boolean;
  underline: boolean;
  color?: string;
}

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

type LineCell =
  | { kind: 'checkboxGroup'; elements: FormElement[] }
  | { kind: 'element'; el: FormElement };

// Only textbox/date/textarea show a border by default on the live canvas (see
// ElementRenderer.tsx's `isInputField` wrapper, which forces borderWidth to 0
// for every other type unless the element's own style explicitly sets one) --
// mirror that convention here rather than relying solely on an explicit
// style.borderWidth, which is normally left unset for these types.
const DEFAULT_BORDER_COLOR: Partial<Record<FormElement['type'], string>> = {
  textbox: 'cbd5e1',
  date: '000000',
  textarea: 'c0c0c0',
};

// Horizontal text inset inside a bordered input, per ElementRenderer.
const INPUT_PAD_X_PX: Partial<Record<FormElement['type'], number>> = {
  textbox: 3,
  date: 4,
  textarea: 4,
};

// Elements that can serve as a checkbox/radio group's leading question text
// when absorbed from the line immediately to its left (see buildLineCells).
const NON_OPTION_ABSORBABLE_TYPES = new Set<FormElement['type']>(['label', 'text', 'heading', 'paragraph']);

// Canvas renders these on a single non-wrapping line that may overflow its
// box (`white-space: nowrap; overflow: visible`), so their frames are widened
// rather than left to wrap when Word's/LibreOffice's font metrics run a hair
// wider than the importer's estimate.
const NOWRAP_TYPES = new Set<FormElement['type']>(['label', 'text', 'heading', 'pagenumber', 'checkbox', 'radio']);

function pxToDxa(px: number): number {
  return Math.round(px * PX_TO_TWIPS);
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function noBorderSet(): BorderSet {
  const none: BorderSide = { style: BorderStyle.NONE };
  return { top: none, bottom: none, left: none, right: none };
}

function hasVisibleBorder(border: BorderSet): boolean {
  return border.top.style !== BorderStyle.NONE;
}

function borderWidthPx(border: BorderSet): number {
  return hasVisibleBorder(border) ? (border.top.size || 0) / 6 : 0;
}

function docxBorderStyle(styleName?: string): DocxBorderStyle {
  switch (styleName) {
    case 'dashed':
      return BorderStyle.DASHED;
    case 'dotted':
      return BorderStyle.DOTTED;
    default:
      return BorderStyle.SINGLE;
  }
}

function hexColor(value: string | undefined, fallback?: string): string | undefined {
  if (!value) return fallback;
  const v = value.replace('#', '').trim();
  return /^[0-9a-fA-F]{6}$/.test(v) ? v.toUpperCase() : fallback;
}

function fillOf(el: FormElement): string | undefined {
  const bg = el.style.background;
  if (!bg || bg === 'transparent') return undefined;
  return hexColor(bg);
}

function resolveElementBorder(el: FormElement): BorderSet {
  const s = el.style;
  const explicit = (s.borderWidth || 0) > 0 && !!s.borderStyle && s.borderStyle !== 'none';

  let widthPx: number;
  let color: string;
  let style: DocxBorderStyle;

  if (explicit) {
    widthPx = s.borderWidth as number;
    color = hexColor(s.borderColor, '000000') as string;
    style = docxBorderStyle(s.borderStyle);
  } else if (DEFAULT_BORDER_COLOR[el.type]) {
    widthPx = 1;
    color = DEFAULT_BORDER_COLOR[el.type] as string;
    style = BorderStyle.SINGLE;
  } else {
    return noBorderSet();
  }

  const size = clamp(Math.round(widthPx * 6), 2, 96); // px -> eighths-of-a-point
  const side: BorderSide = { style, color, size };
  return { top: side, bottom: side, left: side, right: side };
}

// Two elements share a text row when their text CENTRES line up (within about
// half a text line), not when their boxes overlap. The importer gives every
// checkbox/radio a fixed 20px box around ~8px of text, so box overlap chains a
// checkbox to the label rows above and below it -- which would fold an
// unrelated label into the checkbox's paragraph, and a paragraph can only sit
// at one y.
function rowCenterPx(el: FormElement): number {
  const lineH = (typeof el.style.fontSize === 'number' && el.style.fontSize > 0 ? el.style.fontSize : DEFAULT_FONT_PX) * LINE_HEIGHT;
  // Text-like elements are top-aligned on the canvas; everything else is centred in its box.
  return NOWRAP_TYPES.has(el.type) && el.type !== 'checkbox' && el.type !== 'radio'
    ? el.y + Math.min(el.height, lineH) / 2
    : el.y + el.height / 2;
}

function sameLine(a: FormElement, b: FormElement, tolerance = 0.6): boolean {
  const lineOf = (el: FormElement) =>
    (typeof el.style.fontSize === 'number' && el.style.fontSize > 0 ? el.style.fontSize : DEFAULT_FONT_PX) * LINE_HEIGHT;
  return Math.abs(rowCenterPx(a) - rowCenterPx(b)) <= tolerance * Math.min(lineOf(a), lineOf(b));
}

// ---- text styling ---------------------------------------------------------

// "Arial Narrow, sans-serif" / "Segoe UI Symbol, Segoe UI, sans-serif" are CSS
// stacks; Word wants one concrete family, so take the first.
function fontNameOf(fontFamily?: string): string | undefined {
  const first = (fontFamily || '').split(',')[0].replace(/["']/g, '').trim();
  return first || undefined;
}

function textStyleOf(el: FormElement, sizePx?: number): TextStyle {
  const s = el.style;
  const declared = typeof s.fontSize === 'number' && s.fontSize > 0 ? s.fontSize : DEFAULT_FONT_PX;
  return {
    font: fontNameOf(s.fontFamily),
    sizePx: sizePx ?? declared,
    bold: s.fontWeight === 'bold',
    italics: s.fontStyle === 'italic',
    underline: s.textDecoration === 'underline',
    color: hexColor(s.color),
  };
}

function runOptions(ts: TextStyle) {
  return {
    font: ts.font,
    size: Math.max(2, Math.round(ts.sizePx * PX_TO_HALF_POINTS)),
    bold: ts.bold || undefined,
    italics: ts.italics || undefined,
    underline: ts.underline ? {} : undefined,
    color: ts.color,
  };
}

function lineDxa(ts: TextStyle): number {
  return clamp(pxToDxa(ts.sizePx * LINE_HEIGHT), MIN_LINE_DXA, MAX_LINE_DXA);
}

function resolveRuns(runs: ContentRun[] | undefined, ts: TextStyle): TextRun[] {
  const base = runOptions(ts);
  if (!runs || runs.length === 0) return [new TextRun({ ...base, text: '' })];
  return runs.map((run) => {
    if (run.type === 'variable') {
      return new TextRun({ ...base, text: `$${run.fieldKey}$` });
    } else if (run.type === 'textbox' || run.type === 'textarea') {
      const bindingVal = run.fieldKey ? `$${run.fieldKey}$` : (run.value || '');
      return new TextRun({ ...base, text: bindingVal });
    } else if (run.type === 'checkbox') {
      return new TextRun({ ...base, text: `${run.checked ? '☑' : '☐'} ${run.value || 'Checkbox'}` });
    } else if (run.type === 'radio') {
      return new TextRun({ ...base, text: `${run.checked ? '◉' : '○'} ${run.value || 'Option'}` });
    } else {
      return new TextRun({ ...base, text: run.value || '' });
    }
  });
}

function alignmentFor(textAlign?: string) {
  switch (textAlign) {
    case 'center':
      return AlignmentType.CENTER;
    case 'right':
      return AlignmentType.END;
    case 'justify':
      return AlignmentType.BOTH;
    default:
      return AlignmentType.START;
  }
}

// ---- frames ---------------------------------------------------------------

// Word merges consecutive paragraphs whose framePr is identical into ONE frame
// (stacking them vertically), so two elements that share exactly the same
// x/y/width/height would silently collapse together. Tracks the previous
// frame's key per export and nudges by a single twip when it would collide.
class FrameKeyGuard {
  private last = '';

  ensureDistinct(xDxa: number, yDxa: number, wDxa: number, hDxa: number): { x: number; y: number } {
    let y = yDxa;
    while (this.last === `${xDxa}|${y}|${wDxa}|${hDxa}`) y += 1;
    this.last = `${xDxa}|${y}|${wDxa}|${hDxa}`;
    return { x: xDxa, y };
  }
}

function frameOf(box: Box, guard: FrameKeyGuard, rule: (typeof HeightRule)[keyof typeof HeightRule] = HeightRule.ATLEAST) {
  const wDxa = Math.max(MIN_FRAME_DXA, pxToDxa(box.width));
  const hDxa = Math.max(MIN_FRAME_DXA, pxToDxa(box.height));
  const { x, y } = guard.ensureDistinct(Math.round(box.x * PX_TO_TWIPS), Math.round(box.y * PX_TO_TWIPS), wDxa, hDxa);
  return {
    type: 'absolute' as const,
    position: { x, y },
    width: wDxa,
    height: hDxa,
    anchor: { horizontal: FrameAnchorType.PAGE, vertical: FrameAnchorType.PAGE },
    // THROUGH: frames never displace body text/other frames, so the tiny
    // per-page anchor paragraph can't be pushed onto another page.
    wrap: FrameWrap.THROUGH,
    space: { horizontal: 0, vertical: 0 },
    rule,
  };
}

// Per page the body is:  [page break] [that page's frames] [anchor paragraph].
// The two consumers disagree about where a frame lives, and this order
// satisfies both: Word lays a frame out at its own position in document flow
// (so the page break MUST precede the frames -- with the break on the paragraph
// after them, every page's frames pile up on page 1), while LibreOffice binds
// a frame to the paragraph that FOLLOWS it (the drop-cap model), hence the
// trailing anchor on the same page. Both are near-invisible empty paragraphs.
function tinyParagraph(pageBreakBefore: boolean): Paragraph {
  return new Paragraph({
    pageBreakBefore,
    spacing: { before: 0, after: 0, line: MIN_LINE_DXA, lineRule: LineRuleType.EXACT },
    run: { size: 2 },
    children: [],
  });
}

// Border-only / fill-only frame: rect, hline, vline. A paragraph border only
// wraps the paragraph's own height, so the (empty) paragraph is given an
// exact line height equal to the shape's inner height.
function shapeFrame(el: FormElement, guard: FrameKeyGuard): Paragraph | null {
  const fill = fillOf(el);
  const w = Math.max(0, el.style.borderWidth || 0);
  const styled = w > 0 && !!el.style.borderStyle && el.style.borderStyle !== 'none';
  const line = styled
    ? ({
        style: docxBorderStyle(el.style.borderStyle),
        color: hexColor(el.style.borderColor, '000000'),
        size: clamp(Math.round(w * 6), 2, 96),
        space: 0,
      } as BorderSide)
    : null;
  // hline/vline are drawn even when a stray import left borderWidth unset --
  // ElementRenderer defaults them to a 1px solid black stroke.
  const stroke: BorderSide =
    line ?? { style: BorderStyle.SINGLE, color: hexColor(el.style.borderColor, '000000'), size: 6, space: 0 };
  const none: BorderSide = { style: BorderStyle.NONE };

  let border: { top: BorderSide; bottom: BorderSide; left: BorderSide; right: BorderSide };
  let innerHeightPx = el.height;
  if (el.type === 'hline') {
    border = { top: stroke, bottom: none, left: none, right: none };
    innerHeightPx = Math.max(1, el.height);
  } else if (el.type === 'vline') {
    border = { top: none, bottom: none, left: stroke, right: none };
  } else {
    if (!line && !fill) return null; // invisible rect: nothing to draw
    border = line ? { top: line, bottom: line, left: line, right: line } : { top: none, bottom: none, left: none, right: none };
    innerHeightPx = Math.max(1, el.height - 2 * (line ? w : 0));
  }

  const lineHeight = clamp(pxToDxa(innerHeightPx), MIN_LINE_DXA, MAX_LINE_DXA);
  return new Paragraph({
    frame: frameOf(el, guard, HeightRule.EXACT),
    border,
    shading: fill ? { type: ShadingType.CLEAR, fill, color: 'auto' } : undefined,
    spacing: { before: 0, after: 0, line: lineHeight, lineRule: LineRuleType.EXACT },
    run: { size: 2 },
    children: [],
  });
}

// ---- text-bearing frames --------------------------------------------------

function textTopPx(el: FormElement, ts: TextStyle): number {
  if (el.type === 'checkbox' || el.type === 'radio') {
    return el.y + Math.max(0, (el.height - ts.sizePx * LINE_HEIGHT) / 2);
  }
  return el.y;
}

// ElementRenderer draws a checkbox as a 0.75em input, a 0.15em gap, then the
// label. The bare ballot-box glyph is ~1em wide plus a full-width space, so
// used at label size an option would come out ~0.4em wider than on the canvas
// and run into whatever sits to its right. Sizing the glyph and the gap like
// the canvas keeps the option the width it was designed at. The text is still
// exactly "<glyph> <label>", which is what the backend's checkbox parser
// reads; the pieces only differ in run size.
const CHECK_GLYPH_FONT = 'Segoe UI Symbol';
function checkRuns(glyph: string, label: string, ts: TextStyle, boxPx?: number): TextRun[] {
  const base = runOptions(ts);
  // The drawn box of the glyph is ~0.8em, so size it from the checkbox element's
  // own box (clamped) instead of the label font, which on dense forms is tiny.
  const boxGlyphPx = boxPx && boxPx > 0 ? clamp(boxPx, 10, 18) / 0.8 : 0;
  const glyphPx = Math.max(ts.sizePx * 0.75, boxGlyphPx);
  const glyphSize = Math.max(2, Math.round(glyphPx * PX_TO_HALF_POINTS));
  const gapSize = Math.max(2, Math.round(ts.sizePx * 0.45 * PX_TO_HALF_POINTS));
  const runs = [
    new TextRun({ ...base, font: CHECK_GLYPH_FONT, size: glyphSize, text: glyph }),
    new TextRun({ ...base, size: gapSize, text: ' ' }),
  ];
  if (label) runs.push(new TextRun({ ...base, text: label }));
  return runs;
}

function elementRuns(el: FormElement, ts: TextStyle): TextRun[] {
  const base = runOptions(ts);
  switch (el.type) {
    case 'checkbox':
      return checkRuns(el.typeProps?.checkbox?.checked ? '☑' : '☐', el.typeProps?.checkbox?.label || '', ts, Math.min(el.width, el.height));
    case 'radio':
      return checkRuns(el.typeProps?.radio?.checked ? '◉' : '○', el.typeProps?.radio?.label || '', ts, Math.min(el.width, el.height));
    case 'textbox':
    case 'textarea':
    case 'date': {
      const bindingVal = el.bindings?.dynamicField ? `$${el.bindings.dynamicField}$` : (el.bindings?.defaultValue || '');
      return [new TextRun({ ...base, text: bindingVal })];
    }
    case 'dropdown':
      return [new TextRun({ ...base, text: (el.typeProps?.dropdown?.options || [])[0] || '' })];
    case 'signature':
      return [new TextRun({ ...base, text: el.typeProps?.signature?.placeholderText || '' })];
    default:
      return resolveRuns(el.content, ts);
  }
}

function widenedWidthPx(el: FormElement, pageWidthPx: number): number {
  if (!NOWRAP_TYPES.has(el.type)) return el.width;
  if ((el.style.textAlign || 'left') !== 'left') return el.width;
  return Math.max(el.width, pageWidthPx - el.x);
}

// A single bordered/filled input (textbox, date, textarea). Small inputs are
// ONE frame whose paragraph carries the border, so the box also grows if a
// value is longer than the box -- data is never clipped. Vertical centring
// inside a box is done with the border's own top/bottom padding (Word has no
// vertical-align for a framed paragraph); the exact line height absorbs the
// whole-point rounding so the outer box stays the canvas height.
function inputFrame(el: FormElement, guard: FrameKeyGuard): Paragraph[] {
  const border = resolveElementBorder(el);
  const bw = borderWidthPx(border);
  const boxBorder = hasVisibleBorder(border);
  const fill = fillOf(el) ?? (el.type === 'textbox' || el.type === 'textarea' || el.type === 'date' ? 'FFFFFF' : undefined);

  // Shrink the type to fit the box so a value fills it instead of stretching
  // it: the importer leaves textboxes at the 12px default even when the
  // source form's box is only 12px tall.
  const declared = textStyleOf(el).sizePx;
  const fitPx = Math.max(5, (el.height - 2 * bw - 1) / LINE_HEIGHT);
  const ts = textStyleOf(el, Math.min(declared, fitPx));
  const alignment = alignmentFor(el.style.textAlign);
  const padX = INPUT_PAD_X_PX[el.type] ?? 0;
  const lineH = ts.sizePx * LINE_HEIGHT;
  const multiLine = el.type === 'textarea' || el.height > lineH * 2.6 + 2 * bw;

  const indent = { left: pxToDxa(padX + bw), right: pxToDxa(padX + bw) };
  const shading = fill ? { type: ShadingType.CLEAR, fill, color: 'auto' } : undefined;

  if (multiLine) {
    // Tall box: border on a shape-only frame, text on its own top-aligned
    // frame just inside it (a paragraph border can't stretch past its text).
    const out: Paragraph[] = [];
    const s = shapeFrame({ ...el, type: 'rect', style: { ...el.style, background: fill ? `#${fill}` : undefined, borderWidth: boxBorder ? bw : 0, borderStyle: boxBorder ? (el.style.borderStyle && el.style.borderStyle !== 'none' ? el.style.borderStyle : 'solid') : 'none', borderColor: boxBorder ? `#${border.top.color}` : undefined } }, guard);
    if (s) out.push(s);
    const inset = bw + Math.min(2, padX);
    out.push(
      new Paragraph({
        frame: frameOf({ x: el.x + inset, y: el.y + inset, width: Math.max(1, el.width - 2 * inset), height: Math.max(1, el.height - 2 * inset) }, guard),
        alignment,
        indent: { left: pxToDxa(Math.max(0, padX - Math.min(2, padX))), right: pxToDxa(Math.max(0, padX - Math.min(2, padX))) },
        spacing: { before: 0, after: 0, line: lineDxa(ts), lineRule: LineRuleType.EXACT },
        run: { size: runOptions(ts).size },
        children: elementRuns(el, ts),
      })
    );
    return out;
  }

  const innerPx = Math.max(lineH, el.height - 2 * bw);
  const padYPt = clamp(Math.floor(((innerPx - lineH) / 2) * PX_TO_POINTS), 0, 31);
  const lineExactPx = innerPx - 2 * (padYPt / PX_TO_POINTS);
  const spaceX = clamp(Math.round(padX * PX_TO_POINTS), 0, 31);
  const side = (b: BorderSide, space: number): BorderSide => (boxBorder ? { ...b, space } : b);

  return [
    new Paragraph({
      frame: frameOf(el, guard),
      alignment,
      border: boxBorder
        ? {
            top: side(border.top, padYPt),
            bottom: side(border.bottom, padYPt),
            left: side(border.left, spaceX),
            right: side(border.right, spaceX),
          }
        : undefined,
      shading,
      indent,
      spacing: { before: 0, after: 0, line: clamp(pxToDxa(lineExactPx), MIN_LINE_DXA, MAX_LINE_DXA), lineRule: LineRuleType.EXACT },
      run: { size: runOptions(ts).size },
      children: elementRuns(el, ts),
    }),
  ];
}

// Text an element shows (for a label/text/heading: its runs; a placeholder
// stays as `$key$`).
function plainText(el: FormElement): string {
  return (el.content || [])
    .map((r) => (r.type === 'variable' ? `$${r.fieldKey}$` : (r as { value?: string }).value || ''))
    .join('')
    .trim();
}

// Plain text-like element (label / text / heading / paragraph / pagenumber /
// checkbox / radio / dropdown / signature): one frame, no inset. `question`
// is set only for checkbox/radio options (see the note above buildLineCells).
function textFrame(el: FormElement, guard: FrameKeyGuard, pageWidthPx: number, question?: string): Paragraph {
  const ts = textStyleOf(el);
  const isSignature = el.type === 'signature';
  const fill = fillOf(el);
  const runs = elementRuns(el, ts);
  const hidden = question ? [new TextRun({ ...runOptions(ts), text: `${question} `, vanish: true })] : [];
  return new Paragraph({
    frame: frameOf({ x: el.x, y: textTopPx(el, ts), width: widenedWidthPx(el, pageWidthPx), height: el.height }, guard),
    alignment: alignmentFor(el.style.textAlign),
    border: isSignature ? { bottom: { style: BorderStyle.SINGLE, size: 6, color: '000000', space: 1 } } : undefined,
    shading: fill ? { type: ShadingType.CLEAR, fill, color: 'auto' } : undefined,
    spacing: { before: 0, after: 0, line: lineDxa(ts), lineRule: LineRuleType.EXACT },
    run: { size: runOptions(ts).size },
    children: [...hidden, ...runs],
  });
}

function imageFrame(el: FormElement, guard: FrameKeyGuard): Paragraph {
  const src = el.typeProps?.image?.src;
  const frame = frameOf(el, guard);
  if (src && src.startsWith('data:image')) {
    try {
      return new Paragraph({
        frame,
        spacing: { before: 0, after: 0 },
        children: [new ImageRun({ data: src, transformation: { width: Math.round(el.width), height: Math.round(el.height) } } as any)],
      });
    } catch {
      // Malformed image data — degrade to a text placeholder rather than failing the whole export.
    }
  }
  return new Paragraph({ frame, spacing: { before: 0, after: 0 }, children: [new TextRun('[Image]')] });
}

// ---- checkbox / radio groups ---------------------------------------------

// The backend's checkbox matcher (extract_checkbox_groups /
// fill_form_checkboxes in template_ivp New_format.py) has no field binding to
// go on: it reads ONE paragraph's text, takes the words before the ☐/☑ glyphs
// as the question, fuzzy-matches that against patient data, and flips the
// matching option's glyph in place. So an option's paragraph has to contain
// its question. Packing a question and its options into one paragraph (tab
// stops or flowing spaces) can't keep them at their canvas positions -- a tab
// whose preceding text overruns slips every later member a stop, and flowing
// text drifts off its columns -- so each element gets its own frame at its
// exact position instead, and each option's paragraph carries its question as
// a hidden (`vanish`) run: never rendered, never printed, but part of the
// paragraph text the matcher reads. The visible glyph run is the one it flips.
//
// buildLineCells finds the question for each option run: a consecutive run of
// checkbox/radio elements on a line becomes one group, plus any lone label(s)
// immediately preceding it (absorbed by list adjacency rather than x-gap
// distance, so a wide gap between a prompt and its checkboxes can't defeat it).
function buildLineCells(line: FormElement[]): LineCell[] {
  const cells: LineCell[] = [];
  let i = 0;
  while (i < line.length) {
    const el = line[i];
    if (el.type === 'checkbox' || el.type === 'radio') {
      const group: FormElement[] = [el];
      let j = i + 1;
      while (j < line.length && (line[j].type === 'checkbox' || line[j].type === 'radio')) {
        group.push(line[j]);
        j++;
      }
      // Repeatedly pop a still-unclaimed lone label off the end of `cells` so
      // two independent checkbox groups on one line ("Gender: M/F  Smoker:
      // Y/N") each keep only their own adjacent label, not the other's.
      while (true) {
        const prevCell = cells[cells.length - 1];
        if (prevCell && prevCell.kind === 'element' && NON_OPTION_ABSORBABLE_TYPES.has(prevCell.el.type)) {
          cells.pop();
          group.unshift(prevCell.el);
        } else {
          break;
        }
      }
      cells.push({ kind: 'checkboxGroup', elements: group });
      i = j;
    } else {
      cells.push({ kind: 'element', el });
      i++;
    }
  }
  return cells;
}

// ---- real tables ----------------------------------------------------------

function tableElementToTable(el: FormElement): Table | null {
  const t = el.typeProps?.table;
  if (!t) return null;

  const covered = new Set<string>(); // any cell (origin excluded) covered by a merge — omitted entirely,
  // docx auto-inserts the row-continuation cells and columnSpan absorbs the column ones.
  t.merges.forEach((m) => {
    for (let r = m.r; r < m.r + m.rowSpan; r++) {
      for (let c = m.c; c < m.c + m.colSpan; c++) {
        if (r === m.r && c === m.c) continue;
        covered.add(`${r}_${c}`);
      }
    }
  });

  // Column widths: canvas stores percentages of the element's width.
  const totalDxa = Math.max(MIN_FRAME_DXA, pxToDxa(el.width));
  const pct = t.colWidths && t.colWidths.length === t.cols ? t.colWidths : Array.from({ length: t.cols }, () => 100 / t.cols);
  const pctSum = pct.reduce((a, b) => a + b, 0) || 100;
  const colDxa = pct.map((p) => Math.max(MIN_FRAME_DXA, Math.round((p / pctSum) * totalDxa)));
  colDxa[colDxa.length - 1] += totalDxa - colDxa.reduce((a, b) => a + b, 0);

  const cellStyle: TextStyle = { ...textStyleOf(el, TABLE_CELL_FONT_PX), bold: false, italics: false, underline: false };
  const gridSide = { style: BorderStyle.SINGLE, size: 6, color: '999999' };
  const grid = { top: gridSide, bottom: gridSide, left: gridSide, right: gridSide };
  const pad = pxToDxa(TABLE_CELL_PAD_PX);

  const rows: TableRow[] = [];
  for (let r = 0; r < t.rows; r++) {
    const cells: TableCell[] = [];
    for (let c = 0; c < t.cols; c++) {
      const key = `${r}_${c}`;
      if (covered.has(key)) continue;
      const origin = t.merges.find((m) => m.r === r && m.c === c);
      const colSpan = origin ? origin.colSpan : 1;
      const width = colDxa.slice(c, c + colSpan).reduce((a, b) => a + b, 0);
      const content = t.cells?.[r]?.[c] || [];
      cells.push(
        new TableCell({
          columnSpan: colSpan,
          rowSpan: origin ? origin.rowSpan : 1,
          width: { size: width, type: WidthType.DXA },
          verticalAlign: VerticalAlign.CENTER,
          borders: grid,
          margins: { top: pad, bottom: pad, left: pad, right: pad },
          children: [
            new Paragraph({
              spacing: { before: 0, after: 0, line: lineDxa(cellStyle), lineRule: LineRuleType.AT_LEAST },
              children: resolveRuns(content, cellStyle),
            }),
          ],
        })
      );
    }
    rows.push(
      new TableRow({
        height: t.rowHeights?.[r] ? { value: pxToDxa(t.rowHeights[r]), rule: HeightRule.ATLEAST } : undefined,
        children: cells,
      })
    );
  }

  return new Table({
    width: { size: totalDxa, type: WidthType.DXA },
    columnWidths: colDxa,
    layout: TableLayoutType.FIXED,
    float: {
      horizontalAnchor: TableAnchorType.PAGE,
      verticalAnchor: TableAnchorType.PAGE,
      absoluteHorizontalPosition: Math.round(el.x * PX_TO_TWIPS),
      absoluteVerticalPosition: Math.round(el.y * PX_TO_TWIPS),
      overlap: OverlapType.OVERLAP,
    },
    rows,
  });
}

// ---- page + document ------------------------------------------------------

// Draw order mirrors the canvas z-layers in ElementRenderer: rect (1) under
// the rules, under content (2), under inputs/checkbox/radio (10).
function layerOf(el: FormElement): number {
  if (el.type === 'rect') return 0;
  if (el.type === 'hline' || el.type === 'vline') return 1;
  if (el.type === 'table' || el.type === 'image') return 2;
  if (el.type === 'textbox' || el.type === 'textarea' || el.type === 'date' || el.type === 'checkbox' || el.type === 'radio') return 4;
  return 3;
}

function buildPage(elements: FormElement[], pageWidthPx: number): (Paragraph | Table)[] {
  const out: (Paragraph | Table)[] = [];
  const guard = new FrameKeyGuard();
  const visible = elements.filter((e) => e.visible);

  const shapes = visible.filter((e) => e.type === 'rect' || e.type === 'hline' || e.type === 'vline');
  const tables = visible.filter((e) => e.type === 'table');
  const images = visible.filter((e) => e.type === 'image');
  const inline = visible.filter((e) => !['rect', 'hline', 'vline', 'table', 'image'].includes(e.type));

  shapes
    .sort((a, b) => layerOf(a) - layerOf(b) || a.y - b.y || a.x - b.x)
    .forEach((el) => {
      const p = shapeFrame(el, guard);
      if (p) out.push(p);
    });

  images.sort((a, b) => a.y - b.y || a.x - b.x).forEach((el) => out.push(imageFrame(el, guard)));

  // Group per line (vertical-interval overlap, not exact y) purely to find
  // checkbox/radio groups; every other element keeps its own frame.
  const sorted = [...inline].sort((a, b) => a.y - b.y);
  const lines: FormElement[][] = [];
  sorted.forEach((el) => {
    const found = lines.find((line) => line.some((item) => sameLine(item, el)));
    if (found) found.push(el);
    else lines.push([el]);
  });

  const cells: LineCell[] = [];
  lines.forEach((line) => {
    line.sort((a, b) => a.x - b.x);
    cells.push(...buildLineCells(line));
  });

  const cellTop = (c: LineCell) => (c.kind === 'checkboxGroup' ? Math.min(...c.elements.map((e) => e.y)) : c.el.y);
  const cellLeft = (c: LineCell) => (c.kind === 'checkboxGroup' ? Math.min(...c.elements.map((e) => e.x)) : c.el.x);
  const cellLayer = (c: LineCell) => (c.kind === 'checkboxGroup' ? 4 : layerOf(c.el));
  cells
    .sort((a, b) => cellLayer(a) - cellLayer(b) || cellTop(a) - cellTop(b) || cellLeft(a) - cellLeft(b))
    .forEach((cell) => {
      if (cell.kind === 'checkboxGroup') {
        // Leading labels keep their own visible frames; each option gets the
        // group's question as hidden context.
        const isOption = (e: FormElement) => e.type === 'checkbox' || e.type === 'radio';
        const question = cell.elements.filter((e) => !isOption(e)).map(plainText).filter(Boolean).join(' ');
        cell.elements.forEach((e) => out.push(textFrame(e, guard, pageWidthPx, isOption(e) ? question : undefined)));
      } else if (cell.el.type === 'textbox' || cell.el.type === 'textarea' || cell.el.type === 'date') {
        out.push(...inputFrame(cell.el, guard));
      } else {
        out.push(textFrame(cell.el, guard, pageWidthPx));
      }
    });

  tables
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .forEach((el) => {
      const table = tableElementToTable(el);
      if (table) out.push(table);
    });

  return out;
}

export function serializeToDocx(doc: FormDocument): Document {
  const children: (Paragraph | Table)[] = [];

  const pageBase = PAGE_SIZE_PX[doc.pageSize];
  const isLandscape = doc.orientation === 'landscape';
  const pageWidthPx = isLandscape ? pageBase.height : pageBase.width;

  doc.pages.forEach((page, pageIndex) => {
    if (pageIndex > 0) children.push(tinyParagraph(true));
    children.push(...buildPage(page.elements, pageWidthPx));
    children.push(tinyParagraph(false));
  });

  const basePortraitWidth = doc.pageSize === 'A4' ? 11906 : 12240;
  const basePortraitHeight = doc.pageSize === 'A4' ? 16838 : 15840;

  return new Document({
    sections: [
      {
        properties: {
          page: {
            size: {
              width: isLandscape ? basePortraitHeight : basePortraitWidth,
              height: isLandscape ? basePortraitWidth : basePortraitHeight,
            },
            margin: {
              // doc.margins are stored in the same px units as every other
              // FormDocument dimension (see PX_TO_TWIPS above) — not mm, so this
              // uses the same px->twips factor (~15) instead of a mm->twips one.
              top: doc.margins.top * PX_TO_TWIPS,
              right: doc.margins.right * PX_TO_TWIPS,
              bottom: doc.margins.bottom * PX_TO_TWIPS,
              left: doc.margins.left * PX_TO_TWIPS,
            },
          },
        },
        children: children.length ? children : [new Paragraph({ children: [] })],
      },
    ],
  });
}

export async function serializeToDocxBlob(doc: FormDocument): Promise<Blob> {
  const built = serializeToDocx(doc);
  return Packer.toBlob(built);
}
